"use client";

import React from "react";
import { Activity, Database, Waves, Zap, ChevronRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
const sonicSnapshots = [{
  hz: "Capital",
  label: "Private Equity & Investment",
  description: "Through deep market insights and rigorous risk management, we select premium assets globally to create long-term stable value returns for investors.",
  icon: Waves
}, {
  hz: "Innovation",
  label: "Industrial Incubation & Management",
  description: "Combining mature business models with innovative technologies, we provide comprehensive strategic support for high-growth enterprises from seed to expansion stages.",
  icon: Activity
}, {
  hz: "Global",
  label: "Global Resource Network",
  description: "With Singapore as our core and China as our springboard, we build a global resource network to help brands expand their international business footprint.",
  icon: Zap
}];
const vaultData = [{
  id: "STI-01",
  hz: "Finance",
  designation: "STI (China) Group",
  classification: "Core",
  status: "Active",
  factor: "1.000"
}, {
  id: "STI-02",
  hz: "Research",
  designation: "SCI International Research Institute",
  classification: "Intel",
  status: "Stable",
  factor: "0.985"
}, {
  id: "STI-03",
  hz: "Luxury",
  designation: "Luxury Division",
  classification: "Elite",
  status: "Active",
  factor: "1.120"
}, {
  id: "STI-04",
  hz: "Media",
  designation: "Media & MCN Operations",
  classification: "Growth",
  status: "Stable",
  factor: "0.945"
}, {
  id: "STI-05",
  hz: "Tech",
  designation: "Internet Products Division",
  classification: "Innovation",
  status: "Active",
  factor: "1.050"
}, {
  id: "STI-06",
  hz: "Global",
  designation: "International Investment Platform",
  classification: "Strategic",
  status: "Stable",
  factor: "0.998"
}, {
  id: "STI-07",
  hz: "Wine",
  designation: "Premium Wine Trade",
  classification: "Lifestyle",
  status: "Active",
  factor: "1.025"
}];
const technicalNotes = [];
export default function HarmonicScience() {
  const headerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const snapshotsReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const vaultReveal = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="235365" id="新加坡sti集团介绍" className="bg-background text-foreground relative overflow-hidden py-24 sm:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 z-0 animate-pulse opacity-20 mix-blend-multiply blur-[120px]" style={{
      animationDuration: '12s',
      backgroundImage: 'radial-gradient(circle at 50% 50%, hsl(220, 45%, 10%), hsl(43, 70%, 50%), hsl(210, 20%, 98%))'
    }} />

      <div className="relative z-10 mx-auto max-w-5xl px-6 lg:px-8">
        
        {}
        <div ref={headerReveal.ref} className={`transition-all duration-1000 ${headerReveal.isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <div className="mb-4 flex items-center gap-2 text-primary">
            <Activity className="size-5" />
            <span className="font-mono text-sm font-semibold tracking-widest uppercase">About STI Group</span>
          </div>
          <h2 className="font-default text-foreground mb-6 text-4xl font-bold tracking-tight sm:text-5xl">
            Singapore STI Group Introduction
          </h2>
          <p className="text-muted-foreground mb-12 max-w-3xl text-lg leading-relaxed font-default">Singapore Time Innovation Investment Holding Group (STI Group) is a diversified multinational investment holding group with business interests spanning private equity, luxury goods, wine, and the IT/Internet sectors. We are committed to driving growth through the dual engines of capital and innovation, creating a truly influential international investment platform.</p>
        </div>

        {}
        <div ref={snapshotsReveal.ref} className={`mb-16 transition-all duration-1000 delay-200 ${snapshotsReveal.isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {sonicSnapshots.map((snapshot, i) => <Card key={i} data-index={i} className="bg-card/80 border-border/50 backdrop-blur-md transition-all hover:shadow-xl hover:border-primary/30">
                <CardHeader className="pb-4">
                  <div className="mb-2 flex items-center justify-between">
                    <snapshot.icon className="text-primary size-6" />
                    <span className="bg-accent text-accent-foreground px-2.5 py-0.5 font-mono text-xs font-bold uppercase tracking-tighter">
                      {snapshot.hz}
                    </span>
                  </div>
                  <CardTitle className="font-default text-xl font-bold">{snapshot.label}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm leading-relaxed font-default">
                    {snapshot.description}
                  </p>
                </CardContent>
              </Card>)}
          </div>
        </div>

        {}
        <div className="mb-16 max-w-3xl">
          <p className="text-muted-foreground text-lg leading-relaxed font-default">
            Our portfolio includes STI (China) Group, SCI International Research Institute, and specialized divisions in Media, Finance, Internet Products, and Luxury Goods. From incubating high-quality projects to establishing international think tanks between China and Singapore, STI Group remains at the forefront of industrial transformation, providing comprehensive strategic support for high-growth enterprises and brand owners.
          </p>
        </div>

        <Separator className="mb-16 opacity-20 bg-primary" />

        {}
        <div ref={vaultReveal.ref} className={`transition-all duration-1000 delay-300 ${vaultReveal.isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          

          

          <div className="mt-8">
            <Accordion type="single" collapsible className="w-full">
              {technicalNotes.map((note, i) => <AccordionItem key={note.id} value={note.id} data-index={i} className="border-border/50">
                  <AccordionTrigger className="font-default text-sm font-bold hover:no-underline hover:text-primary transition-colors uppercase tracking-wide">
                    {note.title}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm leading-relaxed font-default">
                    {note.content}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
          
          <div className="mt-8 sm:hidden">
             <Button asChild variant="outline" className="w-full rounded-lg border-primary/20">
              <Link to="/GroupSectionPage">
                View All Divisions <ChevronRight className="ml-2 size-4" />
              </Link>
            </Button>
          </div>
        </div>

      </div>
    </section>;
}